import { mutationOptions, queryOptions, type QueryClient } from "@tanstack/react-query";

import { OperatorsForbiddenError, operatorsQueryKeys } from "../../lib/operatorsReactQuery";
import { getOperatorsHttpOrigin } from "../../operators";
import {
  readOperatorsAuthErrorMessage,
  throwOperatorsAuthExpired,
} from "../../operatorsAuthSession";
import type { OkrPeopleResponse, OkrPerson } from "./types";

export const okrsQueryKeys = {
  all: [...operatorsQueryKeys.all, "okrs"] as const,
  people: () => [...okrsQueryKeys.all, "people"] as const,
};

const FORBIDDEN_MESSAGE = "You do not have access to OKRs.";

function throwIfUnauthorized(response: Response): void {
  if (response.status === 401) {
    throwOperatorsAuthExpired();
  }
}

async function throwIfForbidden(response: Response): Promise<void> {
  if (response.status !== 403) {
    return;
  }
  const message = await readOperatorsAuthErrorMessage(response).catch(() => FORBIDDEN_MESSAGE);
  throw new OperatorsForbiddenError(message || FORBIDDEN_MESSAGE);
}

async function readOk(response: Response, fallback: string): Promise<unknown> {
  throwIfUnauthorized(response);
  await throwIfForbidden(response);
  if (!response.ok) {
    throw new Error(
      (await readOperatorsAuthErrorMessage(response).catch(() => fallback)) || fallback,
    );
  }
  return response.json();
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isString(value: unknown): value is string {
  return typeof value === "string";
}

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === "string";
}

function isNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function isPerson(value: unknown): value is OkrPerson {
  if (!isRecord(value)) return false;
  return (
    isString(value.id) &&
    isString(value.displayName) &&
    isString(value.email) &&
    isNullableString(value.title) &&
    isNullableString(value.team) &&
    isNullableString(value.managerId) &&
    typeof value.isActive === "boolean" &&
    typeof value.isArchived === "boolean" &&
    typeof value.isSuspended === "boolean" &&
    typeof value.canOwnOkrs === "boolean"
  );
}

function parsePeople(value: unknown): OkrPeopleResponse {
  if (!isRecord(value) || !Array.isArray(value.people) || !value.people.every(isPerson)) {
    throw new Error("OKR people response was malformed");
  }
  if (value.syncedAt !== null && typeof value.syncedAt !== "string") {
    throw new Error("OKR people response was malformed");
  }
  return { people: value.people, syncedAt: value.syncedAt };
}

async function getJson(path: string, fallback: string): Promise<unknown> {
  const response = await fetch(`${getOperatorsHttpOrigin()}${path}`, {
    credentials: "include",
  });
  return readOk(response, fallback);
}

async function sendJson(
  path: string,
  method: "POST",
  body: unknown,
  fallback: string,
): Promise<unknown> {
  const response = await fetch(`${getOperatorsHttpOrigin()}${path}`, {
    credentials: "include",
    method,
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  return readOk(response, fallback);
}

export function okrsPeopleQueryOptions() {
  return queryOptions({
    queryKey: okrsQueryKeys.people(),
    queryFn: async () =>
      parsePeople(await getJson("/api/v1/okrs/people", "Could not load people.")),
  });
}

function invalidateOkrs(queryClient: QueryClient) {
  return queryClient.invalidateQueries({ queryKey: okrsQueryKeys.all });
}

function isDirectorySyncResult(value: unknown): value is { pulled: number; replaced: number } {
  return isRecord(value) && isNumber(value.pulled) && isNumber(value.replaced);
}

export function syncOkrsDirectoryMutationOptions(queryClient: QueryClient) {
  return mutationOptions({
    mutationFn: async () => {
      const body = await sendJson(
        "/api/v1/okrs/directory/sync",
        "POST",
        {},
        "Could not sync the Workspace directory.",
      );
      if (!isDirectorySyncResult(body)) {
        throw new Error("Directory sync response was malformed");
      }
      return body;
    },
    onSuccess: () => invalidateOkrs(queryClient),
  });
}
